import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Trash2, UserCheck } from "lucide-react";
import { STEPS } from "@/lib/contracts";

export function useApprovers(open = true) {
  const [list, setList] = useState([]);
  const reload = () => api.get("/approvers").then(r => setList(r.data));
  useEffect(() => { if (open) reload(); }, [open]);
  return { list, reload };
}

export function ApproversDialog({ onChange }) {
  const [open, setOpen] = useState(false);
  const { list, reload } = useApprovers(open);
  const [f, setF] = useState({ name: "", title: "", roles: STEPS.map(s => s[0]) });
  const add = async () => {
    if (f.name.trim().length < 2) { toast.error("Ad soyad girin"); return; }
    if (!f.roles.length) { toast.error("En az bir yetki seçin"); return; }
    try { await api.post("/approvers", f); setF({ name: "", title: "", roles: STEPS.map(s => s[0]) }); reload(); onChange?.(); toast.success("Kişi eklendi"); }
    catch (e) { toast.error(e.response?.data?.detail || "Eklenemedi"); }
  };
  const remove = async (id) => {
    try { await api.delete(`/approvers/${id}`); reload(); onChange?.(); } catch (e) { toast.error("Silinemedi"); }
  };
  const toggle = (k) => setF({ ...f, roles: f.roles.includes(k) ? f.roles.filter(x => x !== k) : [...f.roles, k] });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" data-testid="approvers-button"><UserCheck className="w-4 h-4 mr-1" /> Onaylayıcılar</Button></DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="approvers-dialog">
        <DialogHeader><DialogTitle>Hakediş Onay Kişileri</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Ad Soyad" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="approver-name" />
          <Input placeholder="Unvan (ör. Şantiye Şefi)" value={f.title} onChange={e => setF({ ...f, title: e.target.value })} data-testid="approver-title" />
        </div>
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <span className="text-xs text-muted-foreground">Yetkiler:</span>
          {STEPS.map(([k, l]) => <label key={k} className="flex items-center gap-1.5"><Checkbox checked={f.roles.includes(k)} onCheckedChange={() => toggle(k)} data-testid={`approver-role-${k}`} /> {l}</label>)}
          <Button size="sm" onClick={add} className="ml-auto bg-amber-500 hover:bg-amber-600 text-slate-950" data-testid="approver-add">Ekle</Button>
        </div>
        <div className="divide-y divide-border rounded-lg border border-border" data-testid="approver-list">
          {list.length === 0 && <div className="p-3 text-sm text-muted-foreground">Henüz kişi yok.</div>}
          {list.map(a => (
            <div key={a.id} className="flex items-center gap-3 p-2.5 text-sm" data-testid={`approver-${a.id}`}>
              <div className="flex-1"><span className="font-semibold">{a.name}</span> <span className="text-muted-foreground">{a.title}</span></div>
              <div className="flex gap-1">{a.roles.map(r => <span key={r} className="text-[10px] px-1.5 py-0.5 rounded bg-muted">{STEPS.find(s => s[0] === r)?.[1]}</span>)}</div>
              <Button size="icon" variant="ghost" onClick={() => remove(a.id)} data-testid={`approver-delete-${a.id}`}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
